import React, { useCallback, useContext } from 'react';

const AsyncCallbackMiddlewareContext = React.createContext();

/**
 * A provider that provides wrapping functionality to the `useAsyncCallback` hook. When a wrapper is provided, all async callback functions
 * defined with `useAsyncCallback` will be decorated using the `wrap` function.
 *
 * @param {object} props
 * @param {func} props.wrap - A decorator function that accepts an asynchronous function and an optional option argument, and returns the wrapped function.
 *
 * @example
 * <AsyncCallbackMiddlewareProvider wrap={func => (...args) => func(...args).catch((error) => { console.error(error); })}>
 *   <ComponentsThatMightUseAsyncCallbacks />
 * </AsyncCallbackMiddlewareProvider>
 */
export const AsyncCallbackMiddlewareProvider = ({ wrap, children }) => (
  <AsyncCallbackMiddlewareContext.Provider value={wrap}>
    {children}
  </AsyncCallbackMiddlewareContext.Provider>
);

/**
 * A React hook to define asynchronous callback functions in a similar manner to useCallback. The difference is that all functions defined in this way
 * will be decorated when a wrapper function is defined by using `AsyncCallbackMiddlewareProvider`, higher up in the element tree. This can be used to
 * transparently perform error handling or show a busy loader as long as the callback is running.
 * @param {func} callback - The asynchronous function to define
 * @param {array} dependencies - The dependencies of the hook. When one of these dependencies change, the returned function will be recreated.
 * @param {*} options - An arbitrary argument to be passed to the `wrap` function passed to the `AsyncCallbackMiddlewareProvider`.
 * @returns {func} - The asynchronous callback function, possibly decorated.
 */
export const useAsyncCallback = (callback, dependencies, options) => {
  const wrap = useContext(AsyncCallbackMiddlewareContext);
  return useCallback(wrap(callback, options), [...dependencies, wrap]);
};
